import React, { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import DeleteIcon from '@mui/icons-material/Delete';
import CustomCircularProgress from '../customCircularProgress/customCircularProgress.component';
import CustomAutocomplete, { OptionRecord } from '../customAutocomplete/customAutocomplete.component';
import { useCRUD } from '../../../api/crud';
import {
  JOB_STATUS_TRANSITION_ACTIONS_ENDPOINT,
  JOB_STATUS_OPTIONS,
  JOB_NEXT_STATUSES_MAP,
  JOB_KIND_OPTIONS,
  JOB_SERVICES_ENDPOINT,
  SUCCESS_SNACKBAR,
  ERROR_SNACKBAR,
} from '../../../consts';
import { useAuthContext } from '../../../contexts/auth';
import { ServiceRequirementFieldType } from '../../../types/jobs';

// ────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────

type RequiredFieldType = 'V' | 'T' | 'S' | 'I' | 'U' | 'R' | 'A' | 'N';

const FIELD_TYPE_OPTIONS: { value: RequiredFieldType; labelKey: string }[] = [
  { value: 'V', labelKey: 'Counter values' },
  { value: 'T', labelKey: 'Description' },
  { value: 'S', labelKey: 'Services' },
  { value: 'I', labelKey: 'Issues' },
  { value: 'U', labelKey: 'Used parts' },
  { value: 'R', labelKey: 'Rejection reason' },
  { value: 'A', labelKey: 'Attachments' },
  { value: 'N', labelKey: 'Remarks' },
];

// What a task with given services can be required to have. Equipment first:
// it is what the requirements are mostly for.
const REQUIREMENT_FIELD_OPTIONS: { value: ServiceRequirementFieldType; labelKey: string }[] = [
  { value: 'E', labelKey: 'Equipment' },
  { value: 'V', labelKey: 'Counter values' },
  { value: 'T', labelKey: 'Description' },
  { value: 'I', labelKey: 'Issues' },
  { value: 'U', labelKey: 'Used parts' },
  { value: 'A', labelKey: 'Attachments' },
  { value: 'N', labelKey: 'Remarks' },
];

type RequirementRow = {
  // Stable React key - a row has no id until the action is saved.
  key: number;
  fieldType: ServiceRequirementFieldType;
  services: OptionRecord[];
};

type Props = {
  id?: string | number;
  onSuccess: () => void;
};

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

const errorMessageOf = (val: unknown): string => {
  if (!val) return '';
  if (typeof val === 'string') return val;
  if (Array.isArray(val) && val.length > 0) {
    const first = val[0];
    if (typeof first === 'string') return first;
    if (first && typeof first === 'object' && 'message' in first)
      return (first as { message?: string }).message ?? '';
  }
  return '';
};

const extractError = (errors: Record<string, unknown>, key: string): string =>
  errorMessageOf(errors[key]);

const collectAllErrors = (errorData: Record<string, unknown>): string => {
  const messages: string[] = [];
  for (const key of Object.keys(errorData)) {
    const msg = extractError(errorData, key);
    if (msg) messages.push(msg);
  }
  return messages.join(' ');
};

let nextRowKey = 0;
const newRowKey = () => {
  nextRowKey += 1;
  return nextRowKey;
};

const FormSection = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
    <Box>
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
      {description && (
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      )}
    </Box>
    {children}
  </Box>
);

// ────────────────────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────────────────────

const StatusTransitionActionForm: React.FC<Props> = ({ id, onSuccess }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { currentUser } = useAuthContext();
  const { loading, get, create, updatePUT } = useCRUD(JOB_STATUS_TRANSITION_ACTIONS_ENDPOINT);

  const [initialLoading, setInitialLoading] = useState(!!id);
  const [previousStatus, setPreviousStatus] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [obligatoryTypes, setObligatoryTypes] = useState<RequiredFieldType[]>([]);
  const [optionalTypes, setOptionalTypes] = useState<RequiredFieldType[]>([]);
  const [requirements, setRequirements] = useState<RequirementRow[]>([]);
  const [jobKinds, setJobKinds] = useState<Set<string>>(new Set());
  const [errors, setErrors] = useState<Record<string, unknown>>({});
  const canAddAnyJob = Boolean(currentUser?.permissions?.includes('service.add_job'));
  const canAddInternalJobOnly = Boolean(
    currentUser?.permissions?.includes('service.add_internal_job') && !canAddAnyJob,
  );
  const availableJobKindOptions = canAddInternalJobOnly
    ? JOB_KIND_OPTIONS.filter((option) => option.value === 'I')
    : JOB_KIND_OPTIONS;

  useEffect(() => {
    if (!id) return;
    get(id).then((result) => {
      if (result?.success && result.data) {
        const d = result.data;
        setPreviousStatus(d.previous_status ?? '');
        setNewStatus(d.new_status ?? '');
        const fields = (d.required_fields ?? []) as Array<{ field_type: RequiredFieldType; is_required?: boolean }>;
        setObligatoryTypes(Array.from(new Set(fields.filter((rf) => rf.is_required ?? true).map((rf) => rf.field_type))));
        setOptionalTypes(Array.from(new Set(fields.filter((rf) => !(rf.is_required ?? true)).map((rf) => rf.field_type))));
        setRequirements(
          (d.service_requirements ?? []).map(
            (requirement: { field_type: ServiceRequirementFieldType; services: OptionRecord[] }) => ({
              key: newRowKey(),
              fieldType: requirement.field_type,
              services: requirement.services,
            })
          )
        );
        setJobKinds(new Set((d.job_kinds ?? []).map((jk: any) => jk.kind)));
      }
      setInitialLoading(false);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!previousStatus) return;
    const allowedNextStatuses = JOB_NEXT_STATUSES_MAP[previousStatus] ?? [];
    if (allowedNextStatuses.length === 1) {
      setNewStatus(allowedNextStatuses[0]);
    }
  }, [previousStatus]);

  const handleObligatoryChange = (event: SelectChangeEvent<RequiredFieldType[]>) => {
    const value = event.target.value as RequiredFieldType[];
    setObligatoryTypes(value);
    setOptionalTypes((prev) => prev.filter((type) => !value.includes(type)));
  };

  const handleOptionalChange = (event: SelectChangeEvent<RequiredFieldType[]>) => {
    const value = event.target.value as RequiredFieldType[];
    setOptionalTypes(value);
    setObligatoryTypes((prev) => prev.filter((type) => !value.includes(type)));
  };

  const toggleKind = (kind: string) =>
    setJobKinds((prev) => {
      const next = new Set(prev);
      if (next.has(kind)) next.delete(kind);
      else next.add(kind);
      return next;
    });

  const usedRequirementTypes = requirements.map((row) => row.fieldType);
  const unusedRequirementOptions = REQUIREMENT_FIELD_OPTIONS.filter(
    (option) => !usedRequirementTypes.includes(option.value)
  );

  // A new row starts on the first field no other row uses yet.
  const addRequirement = () => {
    if (unusedRequirementOptions.length === 0) return;
    setRequirements((prev) => [
      ...prev,
      { key: newRowKey(), fieldType: unusedRequirementOptions[0].value, services: [] },
    ]);
  };

  const updateRequirement = (key: number, patch: Partial<RequirementRow>) =>
    setRequirements((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)));

  const removeRequirement = (key: number) =>
    setRequirements((prev) => prev.filter((row) => row.key !== key));

  const handleSubmit = async () => {
    setErrors({});
    // A requirement without services would never apply - point at it instead
    // of letting the backend reject the whole form.
    if (requirements.some((row) => row.services.length === 0)) {
      setErrors({
        service_requirements: requirements.map((row) =>
          row.services.length === 0 ? { services: [t('Choose at least one service.')] } : {}
        ),
      });
      enqueueSnackbar(t('Please fix the errors and try again.'), ERROR_SNACKBAR);
      return;
    }

    const payload = {
      previous_status: previousStatus,
      new_status: newStatus,
      required_fields: [
        ...obligatoryTypes.map((field_type) => ({ field_type, checkbox_label: null, is_required: true })),
        ...optionalTypes.map((field_type) => ({ field_type, checkbox_label: null, is_required: false })),
      ],
      service_requirements: requirements.map((row) => ({
        field_type: row.fieldType,
        services: row.services.map((service) => service.id),
      })),
      job_kinds: Array.from(jobKinds).map((kind) => ({ kind })),
    };

    let result;
    if (id) {
      result = await updatePUT({ id, ...payload });
    } else {
      result = await create(payload);
    }

    if (result?.success) {
      enqueueSnackbar(
        t(id ? 'Status transition action saved successfully' : 'Status transition action created successfully'),
        SUCCESS_SNACKBAR,
      );
      onSuccess();
    } else if (result?.errorData) {
      setErrors(result.errorData);
      const message = collectAllErrors(result.errorData) || t('Please fix the errors and try again.');
      enqueueSnackbar(message, ERROR_SNACKBAR);
    } else {
      enqueueSnackbar(t('An unexpected error occurred'), ERROR_SNACKBAR);
    }
  };

  if (initialLoading) return <CustomCircularProgress />;

  const topLevelError =
    extractError(errors, 'non_field_errors') || extractError(errors, 'detail');

  // The backend reports a requirement list error either for the list as a
  // whole or row by row, in the rows' order.
  const requirementErrors = Array.isArray(errors.service_requirements)
    ? (errors.service_requirements as unknown[])
    : [];
  const requirementsError = errorMessageOf(requirementErrors);
  const rowError = (index: number, key: 'field_type' | 'services') => {
    const rowErrors = requirementErrors[index];
    return rowErrors && typeof rowErrors === 'object' && !('message' in rowErrors)
      ? errorMessageOf((rowErrors as Record<string, unknown>)[key])
      : '';
  };

  const renderFieldTypeChips = (selected: RequiredFieldType[], color: 'warning' | 'default') => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {selected.map((value) => (
        <Chip
          key={value}
          size="small"
          color={color}
          variant="outlined"
          label={t(FIELD_TYPE_OPTIONS.find((o) => o.value === value)?.labelKey ?? value)}
        />
      ))}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
      {topLevelError && <Alert severity="error">{topLevelError}</Alert>}

      <FormSection title={t('Transition')}>
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { sm: 'center' }, gap: 2 }}>
          <FormControl fullWidth error={!!extractError(errors, 'previous_status')}>
            <InputLabel>{t('Previous status')}</InputLabel>
            <Select
              value={previousStatus}
              label={t('Previous status')}
              onChange={(e) => {
                setPreviousStatus(e.target.value);
                setNewStatus('');
              }}
            >
              {JOB_STATUS_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {t(o.labelKey)}
                </MenuItem>
              ))}
            </Select>
            {extractError(errors, 'previous_status') && (
              <FormHelperText>{extractError(errors, 'previous_status')}</FormHelperText>
            )}
          </FormControl>

          <ArrowForwardIcon color="disabled" sx={{ display: { xs: 'none', sm: 'block' } }} />

          <FormControl fullWidth error={!!extractError(errors, 'new_status')}>
            <InputLabel>{t('New status')}</InputLabel>
            <Select
              value={newStatus}
              label={t('New status')}
              onChange={(e) => setNewStatus(e.target.value)}
              disabled={!previousStatus}
            >
              {(previousStatus ? JOB_NEXT_STATUSES_MAP[previousStatus] ?? [] : []).map((val) => {
                const option = JOB_STATUS_OPTIONS.find((o) => o.value === val);
                return option ? (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </MenuItem>
                ) : null;
              })}
            </Select>
            {extractError(errors, 'new_status') && (
              <FormHelperText>{extractError(errors, 'new_status')}</FormHelperText>
            )}
          </FormControl>
        </Box>
      </FormSection>

      <Divider />

      <FormSection
        title={t('Fields in the status change window')}
        description={t('Obligatory fields must be filled in for every task, optional ones may be.')}
      >
        <FormControl fullWidth error={!!extractError(errors, 'required_fields')}>
          <InputLabel>{t('Obligatory fields')}</InputLabel>
          <Select
            multiple
            label={t('Obligatory fields')}
            value={obligatoryTypes}
            onChange={handleObligatoryChange}
            renderValue={(selected) => renderFieldTypeChips(selected as RequiredFieldType[], 'warning')}
          >
            {FIELD_TYPE_OPTIONS.filter((o) => !optionalTypes.includes(o.value)).map((o) => (
              <MenuItem key={o.value} value={o.value}>
                <Checkbox checked={obligatoryTypes.includes(o.value)} />
                <ListItemText primary={t(o.labelKey)} />
              </MenuItem>
            ))}
          </Select>
          {extractError(errors, 'required_fields') && (
            <FormHelperText>{extractError(errors, 'required_fields')}</FormHelperText>
          )}
        </FormControl>

        <FormControl fullWidth>
          <InputLabel>{t('Optional fields')}</InputLabel>
          <Select
            multiple
            label={t('Optional fields')}
            value={optionalTypes}
            onChange={handleOptionalChange}
            renderValue={(selected) => renderFieldTypeChips(selected as RequiredFieldType[], 'default')}
          >
            {FIELD_TYPE_OPTIONS.filter((o) => !obligatoryTypes.includes(o.value)).map((o) => (
              <MenuItem key={o.value} value={o.value}>
                <Checkbox checked={optionalTypes.includes(o.value)} />
                <ListItemText primary={t(o.labelKey)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </FormSection>

      <Divider />

      <FormSection
        title={t('Requirements by service')}
        description={t('What a task with a chosen service must have before the job can move to the new status.')}
      >
        {requirements.length === 0 && (
          <Typography variant="body2" color="text.secondary" fontStyle="italic">
            {t('No requirements by service.')}
          </Typography>
        )}

        {requirements.map((row, index) => (
          <Box
            key={row.key}
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { sm: 'flex-start' },
              gap: 1.5,
              p: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <FormControl sx={{ minWidth: { sm: 200 } }} error={!!rowError(index, 'field_type')}>
              <InputLabel id={`requirement-field-${row.key}`}>{t('Required field')}</InputLabel>
              <Select
                labelId={`requirement-field-${row.key}`}
                value={row.fieldType}
                label={t('Required field')}
                onChange={(e) =>
                  updateRequirement(row.key, { fieldType: e.target.value as ServiceRequirementFieldType })
                }
              >
                {REQUIREMENT_FIELD_OPTIONS.filter(
                  (option) => option.value === row.fieldType || !usedRequirementTypes.includes(option.value)
                ).map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.labelKey)}
                  </MenuItem>
                ))}
              </Select>
              {rowError(index, 'field_type') && <FormHelperText>{rowError(index, 'field_type')}</FormHelperText>}
            </FormControl>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <CustomAutocomplete
                multiple
                filterSelectedOptions
                apiConfig={{ url: JOB_SERVICES_ENDPOINT, queryParams: { is_active: 'true' } }}
                value={row.services}
                onChange={(_, newValue) => updateRequirement(row.key, { services: newValue as OptionRecord[] })}
                getOptionLabel={(option) => option.title}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('For tasks with services')}
                    error={!!rowError(index, 'services')}
                    helperText={rowError(index, 'services')}
                  />
                )}
              />
            </Box>

            <Tooltip title={t('Remove requirement')}>
              <IconButton
                aria-label={t('Remove requirement')}
                onClick={() => removeRequirement(row.key)}
                color="error"
                sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>
        ))}

        {requirementsError && <FormHelperText error>{requirementsError}</FormHelperText>}

        <Button
          variant="outlined"
          size="small"
          startIcon={<AddIcon />}
          onClick={addRequirement}
          disabled={unusedRequirementOptions.length === 0}
          sx={{ alignSelf: 'flex-start' }}
        >
          {t('Add requirement')}
        </Button>
      </FormSection>

      <Divider />

      <FormSection title={t('Job kinds')} description={t('Leave all unchecked to apply to every job kind.')}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
          {availableJobKindOptions.map((o) => (
            <FormControlLabel
              key={o.value}
              control={
                <Checkbox
                  checked={jobKinds.has(o.value)}
                  onChange={() => toggleKind(o.value)}
                />
              }
              label={t(o.labelKey)}
            />
          ))}
        </Box>
      </FormSection>

      <Button variant="contained" onClick={handleSubmit} disabled={loading}>
        {loading ? t('Processing...') : t('Submit')}
      </Button>
    </Box>
  );
};

export default StatusTransitionActionForm;
